/* Rhody Reviews api/v1 -- billing domain (Stripe subscription checkout).
   2026-06-23. Wires the Subscribe flow + current-plan display.

   - getPlan(): the signed-in client's current tier + subscription status.
   - getConsentText(): the verbatim, attorney-approved TCPA checkout consent
     paragraph the SPA must show next to the (unchecked) consent box.
   - startCheckout(): creates a Stripe Checkout Session and returns the hosted
     URL for the SPA to redirect to. tcpa_consent is REQUIRED (Hard Rule #6). */
import { request } from './_http';

/* ONE PLAN (Steve, 2026-08-06). 'basic' is the single identifier the API
   accepts; it survives only because it is the already-stored clients.tier
   value, and should be read as "the plan", not "the cheap tier". 'pro' is
   RETIRED and the engine REJECTS it (error: plan_retired) rather than silently
   charging $59 for a screen that said $99. */
export type BillingTier = 'basic';

export interface BillingPlan {
  client_id: string;
  tier: string | null;
  plan_display_name: string | null;
  status: string | null;
  has_active_subscription: boolean;
  stripe_customer_id: string | null;
  /* Front-door copy hint (2026-07-06 monetization gate). Distinguishes a
     never-subscribed tenant ("Start your 14-day trial") from one whose
     trial/subscription lapsed ("Your trial has ended / subscription lapsed").
     Optional: Hopper's backend adds it (brief, Backend task #1). Until it is
     present the /choose-plan copy falls back to `status` (expired/canceled/
     past_due => resubscribe copy), so access is never affected -- this bool
     only drives WORDING, `has_active_subscription` drives the gate. */
  had_prior_subscription?: boolean;
  /* Stored business SMS authorization (clients.sms_authorized). */
  sms_authorized?: boolean;
}

/* ROSCA disclosure + recurring-charge consent line, rendered adjacent to the
   inline card (card-at-send inline, 2026-07-08). Verbatim from the same backend
   wording the hosted Checkout uses (build_trial_disclosure).

   ALWAYS render these server-provided strings, never a locally hardcoded price.
   This is the legally operative text next to the card field, and keeping it
   server-side is what stopped the $49/$59 mismatch from being reproducible in
   the UI layer. */
export interface TierDisclosure {
  disclosure: string;
  consent_line: string;
}

export interface ConsentText {
  version: string;
  business_name: string;
  consent_text: string;
  terms_url: string;
  sms_terms_url: string;
  privacy_url: string;
  /* Optional (added for the inline card-at-send flow). Older backends omit
     these; the inline step guards on their presence. */
  terms_text?: string;
  terms_version?: string;
  trial_days?: number;
  disclosures?: Partial<Record<BillingTier, TierDisclosure>>;
}

/* POST /api/v1/billing/setup-intent -> a Stripe SetupIntent client_secret the
   embedded Payment Element confirms to save a card with NO charge. */
export interface SetupIntentResponse {
  client_secret: string;
  customer_id: string;
}

export interface SubscribeRequest {
  tier: BillingTier;
  locations?: number;
  /* The PaymentMethod id returned by confirming the SetupIntent client-side. */
  payment_method_id: string;
  /* REQUIRED Terms of Service / Privacy acceptance (Hard Rule #6). */
  tcpa_consent: boolean;
  tcpa_consent_version?: string;
  /* OPTIONAL SMS authorization; declining still subscribes. */
  sms_authorized?: boolean;
}

export interface SubscribeResponse {
  subscription_id: string;
  customer_id: string;
  status: string;
  tier: string;
  trial_ends_at: string | null;
  has_active_subscription: boolean;
}

export interface CheckoutRequest {
  tier: BillingTier;
  locations?: number;
  // Required Terms of Service / Privacy acceptance. Checkout is blocked until
  // this is true. This is NOT SMS consent (Fix B1 de-bundle, 2026-06-30).
  tcpa_consent: boolean;
  tcpa_consent_version?: string;
  // Optional, separate SMS-sending authorization. Declining (false) still
  // completes the subscription; SMS sending stays gated per tenant until the
  // business authorizes it here or later in Settings. Backend owner: Hopper.
  sms_authorized?: boolean;
}

export interface CheckoutResponse {
  url: string;
  session_id: string;
}

/* POST /api/v1/billing/portal -> a Stripe billing-portal session URL for the
   self-serve manage/cancel/update-card flow (U3). Backend owner: Hopper. */
export interface BillingPortalResponse {
  url: string;
}

export const billingApi = {
  getPlan(signal?: AbortSignal): Promise<BillingPlan> {
    return request<BillingPlan>('/api/v1/billing/plan', { signal });
  },
  getConsentText(signal?: AbortSignal): Promise<ConsentText> {
    return request<ConsentText>('/api/v1/billing/consent-text', { signal });
  },
  startCheckout(body: CheckoutRequest): Promise<CheckoutResponse> {
    return request<CheckoutResponse>('/api/v1/billing/checkout', {
      method: 'POST',
      body,
    });
  },
  /* Open the Stripe customer billing portal (manage / cancel / update card).
     Returns the portal URL to redirect to. May 404 until Hopper's endpoint is
     live -- the caller handles that gracefully. */
  /* Record the OPTIONAL business SMS authorization outside checkout, with the
     same approved wording + version (POST /billing/sms-authorization). */
  saveSmsAuthorization(body: {
    sms_authorized: boolean;
    tcpa_consent_version: string;
  }): Promise<{ sms_authorized: boolean }> {
    return request<{ sms_authorized: boolean }>('/api/v1/billing/sms-authorization', {
      method: 'POST',
      body,
    });
  },
  openBillingPortal(): Promise<BillingPortalResponse> {
    return request<BillingPortalResponse>('/api/v1/billing/portal', {
      method: 'POST',
    });
  },
  /* Create a SetupIntent so the embedded Payment Element can save a card with
     no charge (inline card-at-send). */
  createSetupIntent(): Promise<SetupIntentResponse> {
    return request<SetupIntentResponse>('/api/v1/billing/setup-intent', {
      method: 'POST',
    });
  },
  /* Create the trialing subscription for the chosen tier from the inline-saved
     card. On success the tenant has an active (trialing) subscription, so the
     held review-request send can fire immediately. */
  createSubscription(body: SubscribeRequest): Promise<SubscribeResponse> {
    return request<SubscribeResponse>('/api/v1/billing/subscribe', {
      method: 'POST',
      body,
    });
  },
};
